<script lang="ts">
export default {
  name: 'AppButtonSelect',
};
</script>

<script setup lang="ts">
import { ref, onMounted, useAttrs } from 'vue';
import { ChevronDown } from 'lucide-vue-next';

const attrs = useAttrs();

const props = withDefaults(
  defineProps<{
    primary?: boolean;
    loading?: boolean;
    disabled?: boolean;
    selectOptions: string[];
    dropdownPosition?: 'top' | 'bottom';
  }>(),
  {
    primary: false,
    loading: false,
    disabled: false,
    selectOptions: () => [],
    dropdownPosition: 'bottom',
  },
);

const emit = defineEmits<{
  'selectOption': [string],
}>();

// -----
// refs
// -----

const selectedOption = ref('');
const dropdown = ref<HTMLElement | null>(null);

// -----------
// lifecycles
// -----------

onMounted(() => {
  selectedOption.value = props.selectOptions[0];
});

// ----------
// functions
// ----------

function handleChooseOption(event: Event) {
  const target = event.target as HTMLAnchorElement;
  selectedOption.value = target.textContent || '';
  dropdown.value?.blur();
}
</script>

<template>
  <div class="tw:relative tw:join tw:flex">
    <button
      class="tw:btn tw:btn-sm tw:join-item tw:text-white"
      :class="{
        'tw:btn-primary': props.primary,
        'tw:btn-disabled': props.disabled,
      }"
      @click="() => emit('selectOption', selectedOption)"
    >
      <span v-if="loading" class="tw:loading tw:loading-spinner"></span>
      {{ selectedOption }}
    </button>
    <div 
      class="tw:dropdown tw:join-item"
      :class="{
        'tw:dropdown-left': attrs.dropdownLeft !== undefined,
        'tw:dropdown-top': props.dropdownPosition === 'top',
        'tw:dropdown-bottom': props.dropdownPosition === 'bottom'
      }"
    >
      <div
        tabindex="0"
        role="button"
        class="tw:btn tw:btn-sm tw:p-1 tw:rounded-tl-none tw:rounded-bl-none tw:opacity-90"
        :class="{
          'tw:btn-primary': props.primary,
          'tw:btn-disabled': props.disabled,
        }"
      >
        <ChevronDown :size="20" class="tw:text-white" />
      </div>
      <ul
        tabindex="0"
        class="tw:absolute tw:dropdown-content tw:z-1 tw:menu tw:bg-base-100 tw:shadow-sm tw:flex-nowrap tw:w-fit-content"
        ref="dropdown"
      >
        <li v-for="option in selectOptions" :key="option">
          <a @click="handleChooseOption" class="tw:text-black tw:w-44">{{ option }}</a>
        </li>
      </ul>
    </div>
  </div>
</template>
